import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    inject,
} from '@angular/core';
import { FILTER_QUERY_SEARCH } from '@wdx/clmi/api-services/models';
import { OperationsFacade } from '@wdx/clmi/api-services/state';
import {
    ActionType,
    EntityOperations,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import {
    EntityPermissionType,
    FeaturesService,
    NavItem,
    OperationsSetup,
    Query,
    QueryExpression,
    WdxDestroyClass,
    stringToBase64,
} from '@wdx/shared/utils';
import { saveAs } from 'file-saver';
import {
    BehaviorSubject,
    Observable,
    combineLatest,
    filter,
    map,
    takeUntil,
} from 'rxjs';
import { ICON_CONTEXT_MENU } from '../../../constants/icons.constants';
import { ActionButton } from '../../../models/action-button.model';
import { ActionButtonMode } from '../../../models/action-buttons-mode.model';
import { InfoCardSearchMode } from '../../../models/info-card-search-mode.model';
import { Privilege } from '../../../models/privilege.model';
import { ExportsFacadeService } from '../../features/exports/services/exports-facade.service';
import { FilterQueryService } from '../../features/form-filters/shared/services/filter-query';
import { ImportFileUploaderService } from '../../features/import-file-uploader/shared/services';
import {
    DOWNLOAD_VAL,
    EXPORT,
    EXPORT_VAL,
    IMPORT,
    IMPORT_EXPORT_ENTITY_FEATURE_FLAGS_MAP,
    IMPORT_VAL,
} from './hybrid-select-menu.constant';
import { ImportTypecodeTemplateApiService } from '@wdx/clmi/clmi-swagger-gen/api-services';

@Component({
    selector: 'clmi-hybrid-select-menu',
    templateUrl: './hybrid-select-menu.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HybridSelectMenuComponent
    extends WdxDestroyClass
    implements OnInit
{
    private operationsFacade = inject(OperationsFacade);
    private exportsFacadeService = inject(ExportsFacadeService);
    private importFileUploaderService = inject(ImportFileUploaderService);
    private featuresService = inject(FeaturesService);
    private importTemplateApiService = inject(ImportTypecodeTemplateApiService);
    private filterQueryService = inject(FilterQueryService);

    @Input() disableExport: boolean;
    @Input() size = 'md';
    @Input() btnClass = 'btn-outline-primary';
    @Input() dropdownMenuImportPrivilege?: Privilege;
    @Input() dropdownMenuExportPrivilege?: Privilege;
    @Input() usePermissions = false;
    private _entityPermissions?: EntityPermissionType[];
    @Input() set entityPermissions(
        permissions: EntityPermissionType[] | undefined,
    ) {
        this._entityPermissions = permissions;
        if (permissions) {
            this.actionButton.dropdownMenu =
                this.actionButton.dropdownMenu?.filter(
                    (item) => item.value !== EXPORT_VAL,
                );
            if (permissions.includes(EntityPermissionType.Export)) {
                this.actionButton.dropdownMenu = [
                    EXPORT,
                    ...this.actionButton.dropdownMenu,
                ];
            }
        }
    }
    private extraQueryExpressions$: BehaviorSubject<QueryExpression[]> =
        new BehaviorSubject<QueryExpression[]>([]);
    get extraQueryExpressions(): QueryExpression[] {
        return this.extraQueryExpressions$.value;
    }
    // component supplied query expressions to be added to those in the filterQueryService
    // likely the only use case is adding search text
    @Input() set extraQueryExpressions(queryExpressions: QueryExpression[]) {
        this.extraQueryExpressions$.next(queryExpressions);
    }
    @Input() set operationsSetup(value: OperationsSetup) {
        this.actionButton.operationsSetup = value;
    }

    operations$: Observable<EntityOperations>;
    operationsIsLoading$: Observable<boolean>;
    operationsHasError$: Observable<boolean>;

    queryView$: Observable<Query>;

    readonly INFO_CARD_SEARCH_MODE = InfoCardSearchMode;
    actionButton: ActionButton = {
        mode: ActionButtonMode.DropdownButtonMenu,
        cySelector: `btn-select-menu-action`,
        dropdownMenu: [],
        icon: ICON_CONTEXT_MENU.icon,
    };

    ngOnInit(): void {
        if (
            !this._entityPermissions &&
            (this.dropdownMenuExportPrivilege || this.usePermissions)
        ) {
            const privOrPerm = this.usePermissions
                ? { permission: EntityPermissionType.Export }
                : {
                      privilege: this.dropdownMenuExportPrivilege,
                  };
            this.actionButton.dropdownMenu.push({
                ...EXPORT,
                ...privOrPerm,
            });
        }

        if (
            Object.keys(IMPORT_EXPORT_ENTITY_FEATURE_FLAGS_MAP).some(
                (key) =>
                    this.filterQueryService?.viewType === key &&
                    this.featuresService.hasFeature(
                        IMPORT_EXPORT_ENTITY_FEATURE_FLAGS_MAP[key],
                    ),
            ) &&
            (this.dropdownMenuImportPrivilege || this.usePermissions)
        ) {
            const privOrPerm = this.usePermissions
                ? { permission: EntityPermissionType.Import }
                : {
                      privilege: this.dropdownMenuExportPrivilege,
                  };
            IMPORT.forEach((item) => {
                this.actionButton.dropdownMenu.push({
                    ...item,
                    ...privOrPerm,
                });
            });
        }

        const entity = this.filterQueryService?.view?.entity;

        if (entity) {
            this.operations$ = this.operationsFacade
                .getOperationsForEntity$(entity)
                .pipe(takeUntil(this.destroyed$));

            this.operationsIsLoading$ = this.operationsFacade
                .getOperationsForEntityIsLoading$(entity)
                .pipe(takeUntil(this.destroyed$));

            this.operationsHasError$ = this.operationsFacade
                .getOperationsHasError$(entity)
                .pipe(takeUntil(this.destroyed$));

            this.queryView$ = this.filterQueryService.view$.pipe(
                takeUntil(this.destroyed$),
                map((view) => view?.filter),
            );
            this.getOperationMenuItem(entity);
        }
    }

    getOperationMenuItem(entityType: SystemEntity) {
        combineLatest([
            this.operations$,
            this.queryView$,
            this.filterQueryService.inBuilder$,
            this.extraQueryExpressions$,
        ])
            .pipe(
                takeUntil(this.destroyed$),
                filter((value) => value !== undefined),
            )
            .subscribe(([op, queryView, inBuilder, extraExpressions]) => {
                if (op?.actions) {
                    op.actions.forEach((action) => {
                        const actionLabel = action.displayName?.value;

                        let actionMenu: NavItem = {
                            label: actionLabel,
                            icon: action.icon,
                        };

                        if (
                            [ActionType.Form, ActionType.NewForm].includes(
                                action.type,
                            )
                        ) {
                            const query: Query = {
                                ...(inBuilder
                                    ? this.filterQueryService.builderQuery
                                    : queryView),
                            };
                            if (extraExpressions.length) {
                                query.expressions = [
                                    ...query.expressions,
                                    ...extraExpressions,
                                ];
                            }

                            // Forward the list view's search-bar value as a top-level initialisation
                            // param. The query blob is base64-encoded so RegData can't see inside it
                            // to tokenise the term; passing it separately lets the bulk endpoints
                            // tokenise it
                            const searchTerm = extractSearchTerm(
                                query.expressions,
                            );

                            actionMenu = {
                                ...actionMenu,
                                formSetup: {
                                    formId: action.formName || action.url,
                                    initialisationParams: {
                                        query: stringToBase64(
                                            JSON.stringify(query),
                                        ),
                                        type: entityType,
                                        ...(searchTerm ? { searchTerm } : {}),
                                    },
                                },
                            };
                        }

                        const existingItemIndex =
                            this.actionButton.dropdownMenu.findIndex(
                                (menuItem) => menuItem.label === actionLabel,
                            );

                        if (existingItemIndex > -1) {
                            this.actionButton.dropdownMenu[existingItemIndex] =
                                actionMenu;
                        } else {
                            this.actionButton.dropdownMenu.push(actionMenu);
                        }
                    });
                }
            });
    }

    onImportIconClicked(navItem: NavItem): void {
        if (navItem?.value === EXPORT_VAL && !this.disableExport) {
            this.onExport();
        }

        if (navItem?.value === IMPORT_VAL) {
            this.onImport();
        }

        if (navItem?.value === DOWNLOAD_VAL) {
            this.onDownloadTemplate();
        }
    }

    onExport(): void {
        const payload = this.getQuery(
            this.filterQueryService?.inBuilder
                ? this.filterQueryService?.builderQuery
                : this.filterQueryService?.query,
        );
        this.exportsFacadeService.doExport({
            queryType: this.filterQueryService?.viewType,
            payload,
        });
    }

    onImport(): void {
        this.importFileUploaderService.openModal(
            this.filterQueryService?.viewType,
        );
    }

    onDownloadTemplate(): void {
        this.importTemplateApiService
            .get(this.filterQueryService?.viewType)
            .subscribe((data: string) => {
                saveAs(
                    new Blob([data], { type: 'application/octet-stream' }),
                    `${this.filterQueryService?.viewType}-template.csv`,
                );
            });
    }

    private getQuery(query: Query): Query {
        if (
            this.filterQueryService?.inBuilder &&
            this.filterQueryService?.builderQuery
        ) {
            return { ...this.filterQueryService.builderQuery };
        }

        if (this.extraQueryExpressions?.length) {
            return { ...query, expressions: this.extraQueryExpressions };
        }

        return query;
    }
}

/**
 * Pull the raw search-bar string out of a Search expression.
 */
function extractSearchTerm(
    expressions: QueryExpression[] | undefined,
): string | undefined {
    const searchExpr = expressions?.find(
        (e) => e?.field === FILTER_QUERY_SEARCH,
    );
    const rawValue = searchExpr?.values?.[0];

    if (typeof rawValue === 'string') {
        return rawValue || undefined;
    }

    const identitySearch = (rawValue as { search?: string } | undefined)
        ?.search;
    return identitySearch || undefined;
}
